import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BASE_URL from "../../BASEURL";

export default function GalleryList() {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [editIndex, setEditIndex] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${BASE_URL}/api/gallery`);
        if (!res.ok) throw new Error("Failed to fetch gallery");
        const json = await res.json();
        setGallery(Array.isArray(json.data) ? json.data : []);
      } catch (err) {
        setError(err.message || "Failed to fetch gallery");
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const handleSaveIndex = async () => {
    if (!editItem) return;
    setSaving(true);
    setSaveError("");
    const id = editItem._id || editItem.id;
    const idx = editIndex === "" ? null : parseInt(editIndex, 10);
    try {
      const res = await fetch(`${BASE_URL}/api/gallery/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ index: idx }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Failed to save index');
      }
      // update local state
      setGallery((prev) => prev.map((g) => ( (g._id || g.id) === id ? { ...g, index: idx } : g )));
      setEditItem(null);
    } catch (err) {
      setSaveError(err.message || 'Failed to save index');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="p-10">Loading...</p>;
  if (error) return <p className="p-10 text-red-500">{error}</p>;

  return (
    <div className="max-w-7xl mx-auto md:p-6 space-y-6">
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-primaryDark">Gallery</h1>
        <Link
          to="/admin/gallerylist/add"
          className="bg-primary text-white px-6 py-2 rounded hover:bg-primaryDark"
        >
          + Add Image
        </Link>
      </div>

      {/* GALLERY GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {gallery.length === 0 && <p className="p-10">No images found.</p>}
        {gallery.map((item) => (
          <div
            key={item.id || item._id || item.title}
            className="bg-white rounded-lg shadow hover:shadow-lg transition"
          >
            <img
              src={item.image}
              alt={item.title}
              className="h-52 w-full object-contain rounded-t-lg"
            />
            <div className="p-4">
              <h3 className="font-semibold text-lg">{item.title}</h3>
              <div className="flex gap-3 mt-3">
                <button
                  className="text-sm px-4 py-1 border border-yellow-500 text-yellow-700 hover:bg-yellow-500 hover:text-white transition rounded"
                  onClick={() => {
                    setEditItem(item);
                    setEditIndex(typeof item.index === 'number' ? String(item.index) : (item.index || ''));
                    setSaveError("");
                  }}
                >
                  Edit Index
                </button>
                <button
                  className="text-sm px-4 py-1 border border-red-500 text-red-500 hover:bg-red-500  hover:text-white transition rounded"
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to delete this image?')) return;
                    const id = item._id || item.id;
                    try {
                      const res = await fetch(`${BASE_URL}/api/gallery/${id}`, { method: 'DELETE' });
                      if (!res.ok) throw new Error('Delete failed');
                      setGallery((prev) => prev.filter((g) => (g._id || g.id) !== id));
                    } catch (e) {
                      alert(e.message || 'Failed to delete');
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editItem && (
        <GalleryListEditModal
          item={editItem}
          value={editIndex}
          setValue={setEditIndex}
          onClose={() => setEditItem(null)}
          onSave={handleSaveIndex}
          saving={saving}
          error={saveError}
        />
      )}
    </div>
  );
}

// Edit modal rendering appended below export (keeps patch minimal and in same file scope)
export function GalleryListEditModal({ item, value, onClose, onSave, saving, error, setValue }) {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="bg-white rounded-lg p-6 z-10 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">Edit Index — {item.title}</h3>
        <label className="block text-sm mb-2">Index (numeric)</label>
        <input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full border p-2 rounded mb-4"
        />
        {error && <p className="text-red-500 mb-2">{error}</p>}
        <div className="flex justify-end gap-3">
          <button className="px-4 py-2 rounded border" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="px-4 py-2 rounded bg-primary text-white" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
